import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotesAuth } from '@/lib/notesAuth';
import { Mail, Lock, Eye, EyeOff, Loader2, ArrowRight, Printer, PencilRuler, UserCheck, ScanLine } from 'lucide-react';
import { TagLogo } from './TagChrome';
import { tagThemeClass } from './tagTheme';

/**
 * TagWorkspaceLogin — neutral, professional sign-in for metriqs.co.in.
 * Uses the Notes auth backend but carries NO Inventique / Notes branding.
 */
export default function TagWorkspaceLogin() {
  const { login, principal, loading: authLoading } = useNotesAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [scanAsk, setScanAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (!authLoading && principal && !principal.is_super) navigate('/', { replace: true }); }, [authLoading, principal, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid email or password.');
    } finally {
      setBusy(false);
    }
  };

  const INPUT = 'w-full rounded-lg border border-input bg-background py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-background';

  return (
    <div className={`tag-scope ${tagThemeClass()} flex min-h-screen w-full`} data-testid="tagws-login-page">
      <div className="relative hidden overflow-hidden border-r border-border bg-noise tagws-hero lg:flex lg:w-1/2">
        <div className="relative z-10 flex flex-col justify-between p-12">
          <div className="flex items-center gap-2.5">
            <TagLogo size={64} />
            <span className="text-xl font-semibold tracking-tight text-foreground">Tag Workspace</span>
          </div>
          <div>
            <h1 className="text-4xl font-semibold leading-tight tracking-tight text-foreground">Create &amp; print<br />jewellery tags.</h1>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">A focused workspace for managing and batch-printing tags — fast, precise, and operator-friendly.</p>
            <ul className="mt-6 space-y-2.5 text-sm text-foreground/80">
              <li className="flex items-center gap-2.5"><span className="grid h-7 w-7 place-items-center rounded-lg bg-[hsl(var(--accent))] text-foreground"><PencilRuler size={14} /></span> Manual tags</li>
              <li className="flex items-center gap-2.5"><span className="grid h-7 w-7 place-items-center rounded-lg bg-[hsl(var(--accent))] text-foreground"><Printer size={14} /></span> Batch printing</li>
              <li className="flex items-center gap-2.5"><span className="grid h-7 w-7 place-items-center rounded-lg bg-[hsl(var(--accent))] text-foreground"><UserCheck size={14} /></span> Operator-friendly</li>
            </ul>
          </div>
          <div className="text-xs text-muted-foreground">Secure sign-in required</div>
        </div>
      </div>

      <div className="flex min-h-screen flex-1 items-center justify-center px-4 py-10 lg:w-1/2">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
          <div className="mb-6 flex items-center gap-2.5 lg:hidden">
            <TagLogo size={52} />
            <span className="text-lg font-semibold tracking-tight text-foreground">Tag Workspace</span>
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">Sign in</h2>
          <p className="mt-1 text-sm text-muted-foreground">Enter your workspace credentials to continue.</p>

          {error && (
            <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/10 px-3.5 py-2.5 text-sm text-destructive" data-testid="tagws-login-error-text">{error}</div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email</label>
              <div className="relative">
                <Mail size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email" className={`${INPUT} pl-9 pr-3`} data-testid="tagws-login-email-input" />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Password</label>
              <div className="relative">
                <Lock size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input type={show ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" autoComplete="current-password" className={`${INPUT} pl-9 pr-10`} data-testid="tagws-login-password-input" />
                <button type="button" onClick={() => setShow((s) => !s)} aria-label="Toggle password visibility" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">{show ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
            <button type="submit" disabled={busy} className="group mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition-colors duration-150 hover:opacity-90 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.99]" data-testid="tagws-login-submit-button">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <>Sign in <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" /></>}
            </button>
          </form>
          <button type="button" onClick={() => { sessionStorage.setItem('tagws_open_scan', '1'); setScanAsk(true); document.querySelector('[data-testid="tagws-login-email-input"]')?.focus(); }}
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[hsl(var(--ring))] bg-[hsl(var(--accent))] px-4 py-3 text-sm font-semibold text-foreground md:hidden" data-testid="tagws-login-scan-button">
            <ScanLine size={16} /> Scan multiple tags
          </button>
          {scanAsk ? <p className="mt-2 text-center text-xs text-muted-foreground md:hidden" data-testid="tagws-login-scan-hint">Sign in above — the scanner opens right after.</p> : null}
          <p className="mt-8 text-xs text-muted-foreground">This is a private workspace. Scan a jewellery tag&rsquo;s QR code to view it without signing in.</p>
        </div>
      </div>
    </div>
  );
}
